import React, { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmModal } from '@ar/components/shared/ConfirmModal';
import { useInvoicePaidEventSyncManuallyMutation } from '@ar/hooks/mutations/InvoiceMutations';
import { FETCH_TRANSACTIONS_QUERY_KEY, FETCH_TRANSACTION_ACTIVITY_QUERY_KEY, FETCH_TRANSACTION_QUERY_KEY } from '@ar/hooks/queries/InvoicesQueries.hook';
import { Transaction } from '@ar/network/AccountsReceivable.network';
import { useSupplierStore } from '@ar/stores/SupplierStore';
import { TrackingEvents } from '@ar/constants/TrackingConstants';
import { getGpoId } from '@v2/utils/GPOUtils';
import { sendTrackingEvent } from '@v2/utils/Tracking';

type UseResyncPaymentActionResult = {
    handleResyncPayment: () => void,
    resyncPaymentConfirmationModal: JSX.Element,
};

export const useResyncPaymentAction = (invoice?: Transaction | null): UseResyncPaymentActionResult => {
    const [invoicePendingConfirmation, setInvoicePendingConfirmation] = useState<Transaction | null>(null);
    const { supplierLoginData } = useSupplierStore();
    const supplierID = supplierLoginData?.supplier_id;
    const gpoId = getGpoId(supplierLoginData);
    const queryClient = useQueryClient();
    const invoicePaidEventSyncManually = useInvoicePaidEventSyncManuallyMutation();

    const closeConfirmationModal = useCallback((): void => {
        setInvoicePendingConfirmation(null);
    }, []);

    const executeResyncPayment = useCallback((targetInvoice: Transaction): void => {
        invoicePaidEventSyncManually.mutate({
            supplierID,
            transactionID: targetInvoice.id,
            gpoId,
        }, {
            onSuccess: () => {
                queryClient.invalidateQueries([FETCH_TRANSACTIONS_QUERY_KEY]);
                queryClient.invalidateQueries([FETCH_TRANSACTION_ACTIVITY_QUERY_KEY]);
                queryClient.invalidateQueries([FETCH_TRANSACTION_QUERY_KEY]);
            },
            onSettled: closeConfirmationModal,
        });
        sendTrackingEvent(TrackingEvents.invoicePaidEventSyncManuallyClicked);
    }, [closeConfirmationModal, gpoId, invoicePaidEventSyncManually, queryClient, supplierID]);

    const handleResyncPayment = useCallback((): void => {
        if (!invoice) return;

        if (invoice.sync) {
            setInvoicePendingConfirmation(invoice);
            return;
        }

        executeResyncPayment(invoice);
    }, [executeResyncPayment, invoice]);

    const handleConfirmResyncPayment = useCallback((): void => {
        if (!invoicePendingConfirmation) return;
        executeResyncPayment(invoicePendingConfirmation);
    }, [executeResyncPayment, invoicePendingConfirmation]);

    return {
        handleResyncPayment,
        resyncPaymentConfirmationModal: <ConfirmModal
            isOpen={!!invoicePendingConfirmation}
            title="Resync payment"
            prompt="This invoice is already marked as synced in Notch. Proceeding will trigger a resync with the external platform."
            confirmLabel="Resync payment"
            isLoading={invoicePaidEventSyncManually.isLoading}
            onClose={closeConfirmationModal}
            onConfirm={handleConfirmResyncPayment}/>,
    };
};
